import { Component, Input, Optional, Self, ViewEncapsulation, forwardRef } from '@angular/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import dayjs from 'dayjs';

@Component({
  selector: 'lib-year-month-picker',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  providers: [
    provideNativeDateAdapter(),
  ],
  standalone: true,
  template: `
  <mat-form-field appearance="outline"
                  [class.ng-valid]="ngControl?.valid"
                  [class.ng-invalid]="ngControl?.invalid"
                  [class.ng-touched]="ngControl?.touched"
                  [class.ng-untouched]="ngControl?.untouched"
                  [class.ng-dirty]="ngControl?.dirty"
                  [class.ng-pristine]="ngControl?.pristine">
    <mat-label>{{label}}</mat-label>
    <input matInput
           [matDatepicker]="dp"
           [max]="maxDate"
           [min]="minDate"
           [value]="value"
           [disabled]="disabled || readonly"
           (click)="(readonly || disabled) ? null : dp.open()"
           (blur)="onBlur()"
           readonly>
    <mat-hint>{{hint}}</mat-hint>
    <mat-datepicker-toggle matIconSuffix [for]="dp" [disabled]="readonly || disabled"></mat-datepicker-toggle>
    <mat-datepicker #dp startView="multi-year" (monthSelected)="setMonthAndYear($event, dp)">
    </mat-datepicker>
  </mat-form-field>
`,
  styles: [`
  :host {
    display: flex !important;
    flex: 1 auto !important;
    margin-right: 0.5rem;

    mat-form-field {
      width: 100%;
    }
  }
`],
  encapsulation: ViewEncapsulation.Emulated,
})
export class YearMonthPickerComponent implements ControlValueAccessor {

  @Input() public label = 'Start Date';
  @Input() public maxDate = new Date();
  @Input() public minDate: Date | null = null;
  @Input() public hint = 'MMN/YYYY';
  @Input() public readonly = false;

  public value: Date | null = null;
  public disabled = false;

  private onChange: (value: Date | null) => void = () => { /* no-op */ };
  private onTouched: () => void = () => { /* no-op */ };

  constructor(@Self() @Optional() public ngControl: NgControl | null) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  public writeValue(value: Date | null): void {
    this.value = value ?? null;
  }

  public registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public setMonthAndYear(normalizedMonthAndYear: Date, datepicker: MatDatepicker<Date>) {
    const newDate = dayjs(normalizedMonthAndYear).date(1).toDate();
    this.value = newDate;
    this.onChange(newDate);
    this.onTouched();
    datepicker.close();
  }

  public onBlur(): void {
    this.onTouched();
  }
}
